'use strict';

const moment = require('moment');
const isEmpty = require('lodash/isEmpty');
const isEqual = require('lodash/isEqual');
const isNull = require('lodash/isNull');
const isObject = require('lodash/isObject');
const { getPartners, getPartnersFees } = require('./partners');

const isProduction = () => process.env.NODE_ENV === 'production';

const commonFeatures = [];
const highPlanFeatures = [
  'VIEW_ANALYTICS',
  'VIEW_SUBSCRIBERS',
  'VIEW_MESSAGES',
  'EPISODE_RECORDING',
  'EPISODE_SCHEDULED',
  'EPISODE_DRIPPING',
  'EPISODE_BULKUPLOAD',
  'EPISODE_PROCESSING',
  'SOUNDCAST_INTRO_OUTRO',
  'SOUNDCAST_INTRO_OUTRO_REPLACE',
  'SOUNDCAST_COUPONS',
  'SOUNDCAST_COUPONS_100POFF',
  'SOUNDCAST_MULTIPLE_PRICING',
  'SOUNDCAST_PRIVATE',
  'SOUNDCAST_FEED_SUBMIT_ONBEHALF',
  'YOUTUBE_TO_PODCAST',
  'MAILCHIMP_INTEGRATION',
  'SOUNDWAVE_VIDEOS',
  'IN_APP_MESSAGES',
  'EMAIL_TO_LISTENERS',
  'NON_PAYMENT_ACCESS_LINK',
  'INTEGRATION_3RDPARTY_ZAPIER',
  'SOUNDCAST_TRACKLIST_LANDING_TOGGLE',
  'ADD_CATEGORY',
  'EDIT_CATEGORY',
  'DELETE_CATEGORY',
  'SET_AS_FEATURED_CATEGORY',
  'UNSET_AS_FEATURED_CATEGORY',
  'EDIT_CATEGORY_TITLE',
  'DND_CATEGORIES',
];

const commonPlanLimits = {
  ALLOWED_ADMINS: Number.MAX_VALUE,
  ALLOWED_PODCAST_FEEDS: Number.MAX_VALUE,
  ALLOWED_YOUTUBE_CONNECT: Number.MAX_VALUE,
  ALLOWED_SOUNDCASTS: Number.MAX_VALUE,
  ALLOWED_EPISODES: Number.MAX_VALUE,
  ALLOWED_SOUNDCAST_PRIVATE_SUBSCRIBERS: Number.MAX_VALUE,
};

const PLAN_TRANSACTION_FEES = {
  free: 20,
  starter: 10,
  basic: 10,
  plus: 0,
  pro: 0,
  platinum: 0,
};

const PLAN_CONTENT_LIMITS = {
  free: {
    soundcasts: 1,
    episodes: 1,
  },
  basic: {
    soundcasts: 1,
    episodes: 50,
  },
  plus: {
    soundcasts: 10,
    episodes: 200,
  },
  pro: {
    soundcasts: Number.MAX_VALUE,
    episodes: Number.MAX_VALUE,
  },
  platinum: {
    soundcasts: Number.MAX_VALUE,
    episodes: Number.MAX_VALUE,
  },
};

const stripeProducts = isProduction() ? {
  // https://dashboard.stripe.com/products/prod_I2UUy9n5yd1TTN
  prod_I2UUy9n5yd1TTN: {
    id: 'starter-monthly-v2',
    name: 'Soundwise Starter Monthly Subscription',
  },
  // https://dashboard.stripe.com/products/prod_I2UXYcMzC4usnm
  prod_I2UXYcMzC4usnm: {
    id: 'starter-annual-v2',
    name: 'Soundwise Starter Annual Subscription',
  },
  //
  prod_DrgoF2dP2RHFnx: {
    id: 'prod_DrgoF2dP2RHFnx',
    name: 'Soundwise Basic Monthly Subscription',
  },
  //
  prod_DrgmtDHvhXFzx7: {
    id: 'prod_DrgmtDHvhXFzx7',
    name: 'Soundwise Basic Annual Subscription',
  },
  // https://dashboard.stripe.com/products/prod_CIfDGkLuKCaFs5
  prod_CIfDGkLuKCaFs5: {
    id: 'plus-monthly-v2',
    name: 'Soundwise Plus Monthly Subscription',
  },
  // https://dashboard.stripe.com/products/prod_CIfFqhoS2m4xaN
  prod_CIfFqhoS2m4xaN: {
    id: 'plus-annual-v2',
    name: 'Soundwise Plus Annual Subscription',
  },
  // https://dashboard.stripe.com/products/prod_CIfGFWSDY3ktD8
  prod_CIfGFWSDY3ktD8: {
    id: 'pro-monthly-v2',
    name: 'Soundwise Pro Monthly Subscription',
  },
  // https://dashboard.stripe.com/products/prod_CIfHWeFWKcVKyh
  prod_CIfHWeFWKcVKyh: {
    id: 'pro-annual-v2',
    name: 'Soundwise Pro Annual Subscription',
  },
  // https://dashboard.stripe.com/products/prod_DUZcpbvwPBKUEX
  prod_DUZcpbvwPBKUEX: {
    id: 'platinum-monthly-v2',
    name: 'Soundwise Platinum Monthly Subscription',
  },
  // https://dashboard.stripe.com/products/prod_DUZZmtqhe9RmTH
  prod_DUZZmtqhe9RmTH: {
    id: 'platinum-annual-v2',
    name: 'Soundwise Platinum Annual Subscription',
  },

  // https://dashboard.stripe.com/products/prod_R9gd1PAVpsiah7
  prod_R9gd1PAVpsiah7: {
    id: 'platinum-quarterly',
    name: 'Soundwise Platinum Quarterly Subscription',
  },
} : { // staging
  // https://dashboard.stripe.com/test/products/prod_I52E4iDJxCV7EH
  prod_I52E4iDJxCV7EH: {
    id: 'starter-monthly-v2',
    name: 'Soundwise Starter Monthly Subscription',
  },
  // https://dashboard.stripe.com/test/products/prod_I52GlxfxKgRxTr
  prod_I52GlxfxKgRxTr: {
    id: 'starter-annual', // TODO -v2 to stripe
    name: 'Soundwise Starter Annual Subscription',
  },
  // https://dashboard.stripe.com/test/products/prod_EoAWoMuhjMaXPh
  prod_EoAWoMuhjMaXPh: {
    id: 'basic-monthly', // TODO -v2 to stripe
    name: 'Soundwise Basic Monthly Subscription',
  },
  // https://dashboard.stripe.com/test/products/prod_EoAZq2mf2Zb1ep
  prod_EoAZq2mf2Zb1ep: {
    id: 'basic-annual', // TODO -v2 to stripe
    name: 'Soundwise Basic Annual Subscription',
  },
  // https://dashboard.stripe.com/test/products/prod_EoAbWEyIkEjZp8
  prod_EoAbWEyIkEjZp8: {
    id: 'plus-monthly-v2',
    name: 'Soundwise Plus Monthly Subscription',
  },
  // https://dashboard.stripe.com/test/products/prod_CIgyYSGqIIkr2s
  prod_CIgyYSGqIIkr2s: {
    id: 'plus-annual-v2',
    name: 'Soundwise Plus Annual Subscription',
  },
  // https://dashboard.stripe.com/test/products/prod_EoAagMNv8MgUE8
  prod_EoAagMNv8MgUE8: {
    id: 'pro-monthly-v2',
    name: 'Soundwise Pro Monthly Subscription',
  },
  // https://dashboard.stripe.com/test/products/prod_DYTz2garKfcQcM
  prod_DYTz2garKfcQcM: {
    id: 'pro-annual-v2',
    name: 'Soundwise Pro Annual Subscription',
  },
  // https://dashboard.stripe.com/test/products/prod_Gz9jj6hJC8jy7c
  prod_Gz9jj6hJC8jy7c: {
    id: 'platinum-monthly-v2',
    name: 'Soundwise Platinum Monthly Subscription',
  },
  // https://dashboard.stripe.com/test/products/prod_Gz9mTNNjoeOwfx
  prod_Gz9mTNNjoeOwfx: {
    id: 'plan_Gz9mdyXPi3Dj3n', // TODO platinum-annual-v2 to stripe
    name: 'Soundwise Platinum Annual Subscription',
  },

  // https://dashboard.stripe.com/test/products/prod_RfsubSVLbymExx
  prod_RfsubSVLbymExx: {
    id: 'platinum-quarterly',
    name: 'Soundwise Platinum Quarterly Subscription',
  },
};

const actualPlans = Object.keys(stripeProducts).map(key => stripeProducts[key].id);
const deprecatedPlans = [
  'starter-monthly', 'starter-annual',
  'pro-monthly', 'pro-annual',
  'plus-monthly', 'plus-annual',
  'platinum-monthly', 'platinum-annual',
];
const getActualStripePlan = (plan) => {
  return deprecatedPlans.includes(plan) ? `${plan}-v2` : plan;
};
const checkStripePlan = (plan) => {
  return actualPlans.includes(plan) || deprecatedPlans.includes(plan);
};

// helper function to copy features except some features
const copyHighPlanFeaturesExcept = exceptFeatures =>
  [...highPlanFeatures].filter(feature => !exceptFeatures.includes(feature));

const getFeatureAccessForClass = (pclass = 1, planPartner = '') => {
  // features for class1 publishers plans
  const class1 = {
    free: [],
    basic: copyHighPlanFeaturesExcept([
      'EPISODE_BULKUPLOAD',
      'EPISODE_SCHEDULED',
      'EPISODE_DRIPPING',
      'SOUNDCAST_COUPONS_100POFF',
      'INTEGRATION_3RDPARTY_ZAPIER',
      'ADD_CATEGORY',
      'EDIT_CATEGORY',
      'DELETE_CATEGORY',
      'SET_AS_FEATURED_CATEGORY',
      'UNSET_AS_FEATURED_CATEGORY',
      'EDIT_CATEGORY_TITLE',
      'DND_CATEGORIES',
    ]),
    plus: copyHighPlanFeaturesExcept([
      'EPISODE_DRIPPING',
      'INTEGRATION_3RDPARTY_ZAPIER',
      'SOUNDCAST_INTRO_OUTRO_REPLACE',
    ]),
    pro: [...highPlanFeatures],
    platinum: [...highPlanFeatures, 'MOBILE_WHITE_LABEL', 'SOUNDWISE_NO_BRANDING', 'TOTAL_ANALYTICS'],
  };

  // features for class2 publishers plans
  const class2 = {
    ...class1,
    basic: [], // no basic is class2 publishers
    starter: [...commonFeatures, 'VIEW_SUBSCRIBERS', 'SOUNDCAST_COUPONS', 'SOUNDCAST_PRIVATE'],
    plus: copyHighPlanFeaturesExcept([
      'SOUNDCAST_MULTIPLE_PRICING',
      'INTEGRATION_3RDPARTY_ZAPIER',
      'MOBILE_WHITE_LABEL',
      'SOUNDCAST_INTRO_OUTRO_REPLACE',
    ]),
  };

  const features = pclass === 2 ? class2 : class1;

  // features for plans that are tied with partner
  const planPartnerFeatures = {
    'appsumo-starter': ['SOUNDCAST_COUPONS_100POFF'],
  };

  // if the planPartner is in the ref merge it with the features
  if (!isEmpty(planPartner) && planPartner in planPartnerFeatures) {
    features[planPartner] = planPartnerFeatures[planPartner];
  }

  return { ...features };
};

const getPlanFeatures = (plan = 'free', v2 = false, planPartner = '') => {
  const sourceFeatureAccess = v2
    ? getFeatureAccessForClass(2, planPartner)
    : getFeatureAccessForClass(1, planPartner);

  const nplan = plan.toLowerCase() in sourceFeatureAccess ? plan.toLowerCase() : 'free';

  // merge planPartner features with plan features if set
  if (!isEmpty(planPartner) && planPartner in sourceFeatureAccess) {
    sourceFeatureAccess[nplan] = sourceFeatureAccess[nplan].concat(
      sourceFeatureAccess[planPartner]
    );
  }

  return sourceFeatureAccess[nplan];
};

const getPlanFeatureLimits = (plan = 'free', v2 = false) => {
  const limits = {
    free: {
      ALLOWED_ADMINS: 1,
      ALLOWED_PODCAST_FEEDS: 1,
      ALLOWED_YOUTUBE_CONNECT: 1,
      ALLOWED_SOUNDCASTS: 1,
      ALLOWED_EPISODES: 1,
    },
    basic: {
      ALLOWED_ADMINS: 1,
      ALLOWED_PODCAST_FEEDS: 1,
      ALLOWED_YOUTUBE_CONNECT: 1,
      ALLOWED_SOUNDCASTS: 1,
      ALLOWED_EPISODES: 50,
    },
    plus: {
      ...commonPlanLimits,
      ALLOWED_YOUTUBE_CONNECT: 2,
      ALLOWED_SOUNDCASTS: 10,
      ALLOWED_EPISODES: 200,
      ALLOWED_SOUNDCAST_PRIVATE_SUBSCRIBERS: 200,
    },
    pro: { ...commonPlanLimits },
    platinum: { ...commonPlanLimits },
  };

  // limits for v2
  const limitsv2 = {
    ...limits,
    starter: {
      ...commonPlanLimits,
      ALLOWED_ADMINS: 1,
      ALLOWED_PODCAST_FEEDS: 1,
      ALLOWED_YOUTUBE_CONNECT: 0,
      ALLOWED_SOUNDCAST_PRIVATE_SUBSCRIBERS: 50,
    },
    plus: {
      ...commonPlanLimits,
      ALLOWED_ADMINS: 2,
      ALLOWED_PODCAST_FEEDS: 5,
      ALLOWED_YOUTUBE_CONNECT: 1,
      ALLOWED_SOUNDCAST_PRIVATE_SUBSCRIBERS: 200,
    },
    pro: {
      ...commonPlanLimits,
      ALLOWED_ADMINS: 5,
    },
  };

  const sourceLimits = v2 ? limitsv2 : limits;
  const nplan = plan.toLowerCase() in sourceLimits ? plan.toLowerCase() : 'free';
  return sourceLimits[nplan];
};

const getPlanFees = (plan = 'free', inpercent = false) => {
  // soundwise transaction fees per plan
  // when charging a publishers customer
  const fees = PLAN_TRANSACTION_FEES;
  const nplan = plan.toLowerCase() in fees ? plan.toLowerCase() : 'free';
  return inpercent ? fees[nplan] / 100 : fees[nplan];
};

const getPlanFeesByPartner = (plan = 'free', partner = 'appsumo', inpercent = false) => {
  const isStarter = isEqual(plan, 'starter');
  const hasPartnerFees = partner in getPartnersFees();
  return isStarter && hasPartnerFees ? getPartnersFees()[partner] : getPlanFees(plan, inpercent);
};

const getPlanLimits = (plan = 'basic', v2 = false) => {
  const plans = PLAN_CONTENT_LIMITS;

  // plans here are only affected new users with
  // planv2=true on their user account
  const plansv2 = {
    ...plans,
    starter: plans['pro'], // similar to pro
    plus: plans['pro'], // similar to pro
  };

  const sourcePlans = v2 ? plansv2 : plans;
  const targetPlan = plan.toLowerCase() in sourcePlans ? plan.toLowerCase() : 'free';
  return sourcePlans[targetPlan];
};

const getPublisherPlan = (publisher = {}) => {
  if (isEmpty(publisher) || !isObject(publisher)) {
    return {
      plan: 'free',
    };
  }

  // if free user just return its active
  if (!('plan' in publisher) && !('current_period_end' in publisher)) {
    return {
      plan: 'free',
    };
  }

  const supportedPartners = ['appsumo', 'stackcommerce', 'appsumo-starter'];
  const { current_period_end = 0, plan = 'free', auto_renewal, frequency, referredBy = '' } = publisher;
  const now = moment().unix();
  const active = current_period_end > now;
  const isPartner = !isEmpty(referredBy) && supportedPartners.includes(referredBy);
  return {
    active,
    plan: active ? plan.toLowerCase() : 'free',
    auto_renewal,
    current_period_end,
    frequency,
    isPartner,
    referredBy,
  };
};

const isPublisherPlan = (plan, publisher) => {
  const { plan: tplan = '' } = getPublisherPlan(publisher);
  return tplan === plan;
};

const isPublisherReferredByPartner = (partner, publisher) => {
  const { created = null, referredBy = '' } = publisher || {};
  return [
    !isNull(created),
    !isEmpty(referredBy),
    getPartners().includes(referredBy),
    isEqual(partner, referredBy),
  ].every(val => !!val);
};

const getPlanStatus = publisher => {
  const {
    plan = '',
    frequency = '',
    auto_renewal: hasAutorenewal = false,
    created = 0,
    current_period_end = 0,
    subscriptionID = '',
    trialEnd = null,
  } = publisher || {};
  const hasValidPlan = [!isEmpty(plan), current_period_end > 0].every(value => !!value);
  const accessEnd = moment.unix(current_period_end);
  const isActivePlan = accessEnd > moment();
  const isTrialPlan = !isNull(trialEnd) && moment.unix(trialEnd) > moment();
  const hasSubscription = !isEmpty(subscriptionID);
  const isAllowedCancelPlan = [
    hasValidPlan, 
    hasSubscription,
    !(isEmpty(frequency) || frequency === 'trial'), // invalid frequecy (? 'trial')
  ].every(val => !!val);
  const isLifetimeAccess = (created > 0) && (current_period_end > 0)
    ? accessEnd.diff(moment.unix(created), 'years') > 98
    : false;
  return {
    plan,
    frequency,
    hasValidPlan,
    isActivePlan,
    isAllowedCancelPlan,
    isLifetimeAccess,
    isTrialPlan,
    hasSubscription,
    hasAutorenewal,
    accessEnd,
  };
};

const getPublisherPlanInfo = (publisher = {}) => {
  const isRefferedByAppsumoStarter =
      isPublisherPlan('starter', publisher) &&
      isPublisherReferredByPartner('appsumo-starter', publisher);
  const {
    plan,
    frequency,
    hasValidPlan,
    isActivePlan,
    isLifetimeAccess,
    isTrialPlan,
    hasSubscription,
    hasAutorenewal,
    accessEnd,
  } = getPlanStatus(publisher || {});

  const getPlanName = () => {
    // from here check if the plan active
    if (hasValidPlan && isActivePlan) {
      if (isRefferedByAppsumoStarter) return 'Essentials';
      if (plan.toLowerCase() === 'starter') return `STARTER (legacy plan)`;
      return plan.toUpperCase();
    }
    return 'FREE';
  };

  const getPlanFrequency = () => {
    if (hasValidPlan && isActivePlan && !isEmpty(frequency)) {
      if (isRefferedByAppsumoStarter) return '';
      return `- ${frequency.toUpperCase()}`;
    }

    return '';
  };

  const getPlanState = () => {
    if (hasValidPlan && isActivePlan) {
      if (isRefferedByAppsumoStarter || isLifetimeAccess) return '(Lifetime Access)';

      // remove grace period to reveal actual expiration date
      const endDateFormat = accessEnd.subtract(1, 'day').format('MMM DD, YYYY');

      // display if its trial
      if (isTrialPlan) {
        return `(Trial ends on ${endDateFormat})`;
      }

      if (hasAutorenewal) {
        return `(Current subscription will renew on ${endDateFormat})`;
      }

      return `(Current subscription will end on ${endDateFormat})`;
    }

    return '';
  };

  const planName = getPlanName();
  const planState = getPlanState();
  const planFrequency = getPlanFrequency();
  const planNameLong = `Soundwise ${getPlanName()}${isEmpty(planFrequency) ? '' : ` ${planFrequency}`}`;
  const planNameWithState = `${planNameLong}${isEmpty(planState) ? '' : ` ${planState}`}`;
  return {
    isActivePlan,
    isTrialPlan,
    hasSubscription,
    planName,
    planState,
    planNameLong,
    planNameWithState,
  };
};

const isPublisherPlatinum = publisher => {
  const { active = false, plan = 'free' } = getPublisherPlan(publisher);
  return active && isEqual(plan.toLowerCase(), 'platinum');
};

const isPublisherClass2 = (publisher = {}) => {
  const { class: publisherClass = 1 } = publisher || {};
  const PUBLISHER_CLASS_2 = 2;
  return publisherClass === PUBLISHER_CLASS_2;
};

const getSoundwiseProductInfo = (product = '') => {
  return product in stripeProducts ? stripeProducts[product] : {};
};

const getPlanFeatureAvailability = (publisher, feature) => {
  const class2 = isPublisherClass2(publisher);
  const sourceFeatureAccess = class2 ? getFeatureAccessForClass(2) : getFeatureAccessForClass(1);

  // find out where the feature is available
  const availableToPlans = Object.keys(sourceFeatureAccess).filter(plan => {
    const platFeatures = sourceFeatureAccess[plan];
    return platFeatures.includes(feature);
  });

  return availableToPlans;
};

const canPublisherAccessFeature = (publisher = {}, targetFeature = '') => {
  // publisher prop should exist
  if (isEmpty(targetFeature) || isEmpty(publisher)) {
    return false;
  }

  const { plan } = getPublisherPlan(publisher);
  const v2 = isPublisherClass2(publisher);
  const sourceFeatureAccess = getPlanFeatures(plan, v2);

  // the targetFeature should fall to specific plan
  if (!sourceFeatureAccess.includes(targetFeature)) {
    return false;
  }

  return true;
};

module.exports = {
  getPlanFeatures,
  getPlanFeatureLimits,
  getPlanFees,
  getPlanFeesByPartner,
  getPlanLimits,
  getPlanStatus,
  getPublisherPlan,
  getPublisherPlanInfo,
  isPublisherPlan,
  isPublisherReferredByPartner,
  isPublisherPlatinum,
  isPublisherClass2,
  getSoundwiseProductInfo,
  getPlanFeatureAvailability,
  canPublisherAccessFeature,
  getActualStripePlan,
  checkStripePlan,
};
